import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { Icon, LogoMark } from "./icons";
import { chooseLocale, useTranslation } from "../i18n";
import { LANGUAGE_NAMES, LOCALES } from "../i18n/locale";
import { LEGAL_DOCUMENTS } from "../lib/legal";
import { Dropdown, Input, cx } from "./ui";

/*
 * The sign-in and sign-up pages share this file rather than one importing the
 * other's page module, which is what they did before — a page exporting layout
 * for a sibling page is the kind of import that survives until someone deletes
 * the wrong file.
 */

export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  emblem,
  announce = false,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Optional: the two-factor step has nowhere to send you but back. */
  footer?: ReactNode;
  /**
   * Replaces the mark-and-wordmark lockup at the top of the column. The consent
   * screen is the one caller: it draws the client and the platform as a pair,
   * and a Maxlayer lockup above a second Maxlayer mark would say the name twice
   * in the first two things on the page. Not a link, unlike the lockup —
   * nothing on a consent screen should invite leaving mid-request.
   */
  emblem?: ReactNode;
  /** Used for a completed reset, where the submit control has disappeared. */
  announce?: boolean;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { t } = useTranslation("auth");

  // "Maxlayer Console", matching the base title in index.html and the shape
  // the operator console uses ("| Maxlayer Operations"). The marketing site is
  // "Maxlayer Cloud" in the same slot; a tab title says which surface this is.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | Maxlayer Console`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  useEffect(() => {
    if (!announce) return;
    const frame = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [announce, title]);

  return (
    /*
     * One column, centred, on the bare canvas.
     *
     * This was two halves: the form on the left and a brand panel on the right
     * carrying a rendered globe, a wash of light that drifted on a 38-second
     * loop, and the marketing site's four pillars. It went for the sake of the
     * flow this page is now part of. The community signs in by sending a
     * visitor here to be asked and taking them straight back, and its own
     * sign-in is a heading and one button in a centred column — so arriving at
     * a split screen with a planet on it read as leaving one product for
     * another, which is precisely what the redirect flow was built to avoid.
     * Both pages are the same shape now, and a visitor crossing between them
     * sees the canvas change and nothing else.
     *
     * Nothing moves. The globe redrew itself every frame and the wash was
     * animated; both were texture for a panel that had nothing else in it, and
     * there is no panel. What is left is one still light behind the mark —
     * `auth-light`, the sign-in page's copy of the shell's own `shell-light` —
     * because a near-black page with no light on it at all reads as switched
     * off rather than as calm.
     *
     * Still one continuous canvas with rules drawn on it, not a card. The form
     * is the only thing on the page, so a box around it would be a border
     * between the page and its own contents; what separates the form from the
     * canvas is space, which a single column has in every direction.
     */
    <div className="auth-light flex min-h-dvh flex-col bg-canvas">
      {/*
        Above the column, spanning the full width. No organization to scope by
        here, because nobody has signed in, so this shows the platform-wide
        notices only. That is precisely the set this page needs: somebody who
        cannot sign in is asking whether it is them or us, and this is the only
        screen that can answer.
      */}
      <AnnouncementBanner orgId={undefined} />

      <LanguageSwitch />

      {/*
        `my-auto` on the block rather than `justify-center` on the column, and
        the difference only shows on the sign-up page — the one form tall
        enough to outgrow a laptop viewport. A centred flex item taller than its
        container overflows *both* ends, and a scroll container cannot reach
        above its own start, so the top of that form was unreachable. Auto
        margins collapse to nothing when there is no spare height to share out,
        and the block flows from the top like any other document.
      */}
      <main
        aria-labelledby="auth-title"
        className="flex flex-1 flex-col px-5 py-10 sm:px-8 sm:py-14"
      >
        {/* 25rem rather than `max-w-sm`. The controls are 44px tall and a
            24rem column around them reads as a form that outgrew its box. */}
        <div className="mx-auto my-auto w-full max-w-[25rem]">
          {/* The mark and the wordmark, one lockup, centred. A "Customer
              dashboard" line sat under the name to say which console this is —
              the operator console signs in on the same dark canvas in amber, and
              amber alone was a thin thing to bet on with both open. The tab
              title carries "Maxlayer Console" now and the eyebrow below says
              "Console access", so the lockup is back to the name alone. */}
          <div className="flex justify-center">
            {emblem ?? (
              <Link to="/" className="inline-flex items-center gap-2.5">
                <LogoMark className="size-8" />
                <span className="text-[1.0625rem] font-medium tracking-[-0.022em] text-fg">
                  Maxlayer
                </span>
              </Link>
            )}
          </div>

          <div className="mt-10 text-center sm:mt-12">
            <p className="micro text-accent">{t("consoleAccess")}</p>
            <h1
              ref={titleRef}
              id="auth-title"
              tabIndex={announce ? -1 : undefined}
              className="mt-4 text-title"
            >
              {title}
            </h1>
            {subtitle && (
              /* Narrower than the form. A subtitle set to the column's full
                 width breaks into two lines of very unequal length; capped a
                 little short of it, the break lands nearer the middle. */
              <p className="mx-auto mt-3 max-w-[22rem] text-sm leading-6 text-fg-muted">
                {subtitle}
              </p>
            )}
          </div>

          <div className="mt-9 sm:mt-10">{children}</div>

          {footer && <div className="mt-8 text-center text-sm text-fg-muted">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

/**
 * Every language the dashboard has, on every signed-out screen, because this is
 * where somebody who cannot read the page arrives first. Choosing one is
 * remembered in the cookie the marketing site shares.
 *
 * At the top rather than under the form: on a first visit the consent bar is
 * fixed over the bottom of the viewport, and the switch has to be reachable
 * before that question is answered.
 */
function LanguageSwitch() {
  const { t, i18n } = useTranslation("common");
  if (LOCALES.length < 2) return null;

  /*
   * A menu naming the language the page is in, with the others behind it.
   * The row of names it replaces showed the current one as plain text and the
   * rest as buttons — the same weight as a trail of links — so it read as
   * navigation rather than as a setting with a value.
   */
  return (
    <div className="flex justify-end px-5 pt-4 sm:px-8">
      <Dropdown
        align="end"
        trigger={
          <>
            <Icon name="globe" className="size-3.5 shrink-0 text-fg-subtle" />
            <span lang={i18n.language}>{LANGUAGE_NAMES[i18n.language] ?? i18n.language}</span>
            <Icon name="chevron-down" className="size-3 shrink-0 text-fg-subtle" />
          </>
        }
        triggerClassName="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-fg-muted transition-colors duration-200 ease-smooth hover:bg-raised hover:text-fg"
        menuClassName="min-w-44"
      >
        <p className="micro px-2.5 pt-1.5 pb-1">{t("language.label")}</p>
        {LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            lang={locale}
            aria-current={locale === i18n.language ? "true" : undefined}
            onClick={() => void chooseLocale(locale)}
            className={cx(LANGUAGE_ITEM, locale === i18n.language && "text-fg")}
          >
            <Icon
              name="check"
              className={cx("size-3.5 shrink-0", locale === i18n.language ? "text-accent" : "opacity-0")}
            />
            {LANGUAGE_NAMES[locale]}
          </button>
        ))}
      </Dropdown>
    </div>
  );
}

const LANGUAGE_ITEM =
  "flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-[13px] text-fg-muted transition-colors duration-200 ease-smooth hover:bg-fg/8 hover:text-fg";

/**
 * The one-time-code field: set large, centred and monospaced, because a code is
 * transcribed digit by digit from somewhere else rather than typed from memory.
 *
 * Shared by the two-factor challenge and the password-reset code, which each had
 * their own before — one a hand-rolled `<input>` filled `bg-surface` inside a
 * `bg-surface` panel, so the field had no visible edge at all.
 *
 * Numeric codes are digit-filtered and capped here rather than at each call
 * site, so `onChange` receives a value that is already clean.
 */
export function CodeInput({
  id,
  label,
  value,
  onChange,
  numeric = true,
  disabled,
  autoFocus,
}: {
  id: string;
  /** Visually hidden: the copy around the field already names it. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Recovery codes are neither six characters nor digits. */
  numeric?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation("auth");
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode={numeric ? "numeric" : "text"}
        autoComplete="one-time-code"
        spellCheck={false}
        autoFocus={autoFocus}
        disabled={disabled}
        required
        maxLength={numeric ? 6 : 40}
        value={value}
        onChange={(event) =>
          onChange(
            numeric
              ? event.target.value.replace(/\D/g, "").slice(0, 6)
              : event.target.value.trimStart(),
          )
        }
        placeholder={numeric ? "000000" : t("password.recoveryPlaceholder")}
        className={cx(
          "w-full rounded-md border border-line bg-surface py-3.5 text-center font-mono text-fg transition-colors duration-200 ease-smooth placeholder:text-fg-subtle focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none disabled:cursor-not-allowed disabled:text-fg-subtle",
          // Wide tracking reads as separated digits; a recovery code is a word
          // and spacing it out makes it harder to check against the paper.
          numeric ? "text-xl tracking-[0.4em] indent-[0.4em]" : "text-base",
        )}
      />
    </>
  );
}

/**
 * What the person in front of this form is agreeing to.
 *
 * On both sign-up and sign-in, and the verb changes between them because the
 * Terms treat the two as different acts. §1 binds you by "creating an account,
 * deploying an application, or otherwise accessing or using the Services" — so
 * signing in is covered by the third clause rather than the first, and a
 * sign-in page reading "by creating an account" would be describing something
 * the reader is not doing.
 *
 * Not on the password-reset flow. Recovering access to an account you already
 * hold agrees to nothing that was not already agreed.
 *
 * A sentence rather than a checkbox, and the difference is honesty rather than
 * fashion. A checkbox says the platform captured your consent; nothing here
 * records it, and the API stores no acceptance timestamp. The Terms form the
 * contract by conduct, the sentence discloses that, and a tickbox implying an
 * audit trail that does not exist would be the one dishonest element on the
 * page.
 *
 * The links open in a new tab because the alternative is destroying a
 * half-filled form to go and read a privacy policy.
 *
 * Centred, like everything in the column that is not a field. It was set
 * ragged-left when the whole desktop column was, and once the heading and the
 * footer moved to the centre it was the one line on the page still hanging off
 * the left edge — under a button that spans the full width, which made it
 * look like a caption that had slipped.
 */
export function LegalNotice({
  action,
}: {
  action: "creatingAccount" | "signingIn";
}) {
  const { t } = useTranslation("auth");
  return (
    <p className="text-center text-xs leading-5 text-fg-subtle">
      {t(`legal.${action}`)}{" "}
      {LEGAL_DOCUMENTS.map((document, index) => (
        <span key={document.href}>
          {index > 0 &&
            (index === LEGAL_DOCUMENTS.length - 1 ? t("legal.lastSeparator") : t("legal.separator"))}
          <a
            href={document.href}
            target="_blank"
            rel="noreferrer noopener"
            className="text-fg-muted underline underline-offset-2 transition-colors duration-200 ease-smooth hover:text-fg"
          >
            {t(`legal.${document.key}`)}
          </a>
        </span>
      ))}
      {t("legal.end")}
    </p>
  );
}

/** Password field with a show/hide toggle and a caps-lock warning. */
export function PasswordInput({
  id,
  autoComplete,
  value,
  onChange,
  minLength,
  size = "xl",
  surface = "canvas",
}: {
  id: string;
  autoComplete: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  minLength?: number;
  /** Both default to the auth surface, because auth is the only caller. */
  size?: "md" | "xl";
  surface?: "panel" | "canvas";
}) {
  const { t } = useTranslation("auth");
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  /*
   * Read from the keyboard event rather than tracked as state across keystrokes:
   * `getModifierState` is the browser's own answer, so it stays right when caps
   * lock is toggled while the field is not focused.
   */
  function checkCapsLock(event: React.KeyboardEvent<HTMLInputElement>) {
    setCapsLock(event.getModifierState("CapsLock"));
  }

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Input
          id={id}
          size={size}
          surface={surface}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          required
          minLength={minLength}
          value={value}
          onChange={onChange}
          onKeyUp={checkCapsLock}
          onKeyDown={checkCapsLock}
          onBlur={() => setCapsLock(false)}
          className="pr-20"
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          // The label says which way the toggle goes; the icon alone is
          // ambiguous about whether it describes the state or the action.
          aria-label={visible ? t("password.hidePassword") : t("password.showPassword")}
          className="absolute inset-y-0 right-0 flex items-center gap-1.5 pr-3 pl-2 text-xs text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
        >
          <Icon name={visible ? "eye-off" : "eye"} className="size-4" />
          {visible ? t("password.hide") : t("password.show")}
        </button>
      </div>

      {capsLock && (
        <p
          role="status"
          className="flex items-center gap-1.5 text-xs text-warn"
        >
          <Icon name="warning" className="size-3.5 shrink-0" />
          {t("password.capsLock")}
        </p>
      )}
    </div>
  );
}

/*
 * Advisory only. The server enforces a minimum length and nothing else, so this
 * is here to nudge rather than to gate — a meter that blocks submission on a
 * rule the API does not have is a meter that lies about the requirements.
 */
const STRENGTH_KEYS = ["", "weak", "fair", "good", "strong"] as const;

/** 0 means "does not meet the minimum yet", which the field's own error says. */
export function passwordScore(password: string, minLength: number): number {
  if (password.length < minLength) return 0;

  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) =>
    re.test(password),
  ).length;

  // Length carries more than variety: a long passphrase of one case beats a
  // short one with a symbol bolted on, and scoring it the other way pushes
  // people toward exactly the passwords that are easiest to guess.
  if (password.length >= minLength + 8 && variety >= 2) return 4;
  if (password.length >= minLength + 4 || variety >= 3) return 3;
  if (variety >= 2) return 2;
  return 1;
}

export function PasswordStrength({
  password,
  minLength,
}: {
  password: string;
  minLength: number;
}) {
  const { t } = useTranslation("auth");
  const score = passwordScore(password, minLength);

  /*
   * Nothing until the minimum is met. Below it the field is already showing
   * "Use at least 8 characters", and a meter reading "too short" underneath
   * that is the same sentence twice in two visual languages.
   */
  if (score === 0) return null;

  const tone = score <= 2 ? "bg-warn" : "bg-mint";

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1" aria-hidden="true">
        {[1, 2, 3, 4].map((segment) => (
          <span
            key={segment}
            className={`h-0.5 flex-1 transition-colors duration-200 ease-smooth ${
              segment <= score ? tone : "bg-line"
            }`}
          />
        ))}
      </div>
      {/* Announced rather than only drawn, so the meter is not invisible to a
          screen reader the way a row of coloured bars would be. */}
      <p role="status" className="text-xs text-fg-subtle">
        {score > 0 && t(`password.strength.${STRENGTH_KEYS[score] as Exclude<(typeof STRENGTH_KEYS)[number], "">}`)}
      </p>
    </div>
  );
}
